
// DANVA task section 8/9: a clear provider/adapter boundary between DANVA
// MODEL DATA (object types, attributes, code lists, relations, defaults,
// exchange schema references) and everything else in PointView (UI, domain
// data, validation, export). Nothing outside this file should ever need to
// know WHERE model data actually came from - a locally-bundled official
// model package, an official XSD/documentation extraction, a cache, or (if
// DANVA ever publishes one) a real API. Today, per this project's own
// research (see the task's own final report, section A): DANVA distributes
// Danvand/Dandas/Fælles Kernemodel exclusively through the licensed,
// login-gated "DANVA-Reolen" portal - there is no public API, and no
// officially-licensed model files exist anywhere in this repository or
// development environment yet. This file is therefore deliberately a REAL,
// working provider contract with an HONEST "no model loaded" default
// implementation - not a fake API client speculatively built for a service
// that does not exist (task section 9: "Byg ikke et stort hypotetisk remote
// API-framework").
//
// Shape a real model definition is expected to have, once one is actually
// loaded (documented here as the CONTRACT, not invented DANVA content):
//
//   ModelDefinition = {
//     model: string,          // e.g. "Danvand" - the official model family name
//     version: string,        // e.g. "2.1.2" - exact, never assumed/rounded
//     objectTypes: [{
//       id: string,            // the official DANVA object type identifier
//       label: string,         // the official DANVA display name
//       geometryTypes: string[], // which PointView geometry.type values this
//                                 // object type is compatible with - e.g.
//                                 // ["LineString"] for a pipe run, ["Point"]
//                                 // for a valve/node - see DanvaCompatibility.js
//       attributes: [{
//         name: string,         // official attribute name
//         label: string,        // official display label
//         dataType: string,     // "string" | "number" | "boolean" | "date" | "code" | ...
//         required: boolean,
//         codeListId: string | null,  // references a CodeList below, if dataType==="code"
//         defaultValue: any | null,
//         editable: boolean,
//         constraints: object | null,  // e.g. {min, max, pattern} - model-defined
//       }],
//       relations: [{
//         name: string,          // e.g. "Fra"/"Til"
//         relationType: string,  // official DANVA relation type
//         targetObjectTypes: string[], // which object types are valid targets
//         cardinality: string,   // e.g. "0..1" | "1" | "0..*"
//         required: boolean,
//       }],
//     }],
//     codeLists: {
//       [codeListId]: [{code: string, label: string}],
//     },
//   }
//
// Nothing in this file invents any of the above CONTENT - only the shape.
export const DANVA_MODEL_CONTRACT_VERSION = 1;

// The provider surface every DANVA-aware UI/domain-logic module in this
// project talks to. Deliberately small (task section 8: "Vælg selv
// interface efter analyse af de faktiske data" - not a prescribed method
// list) - every method here is something the panel/registration flow/
// validation/export code genuinely needs to ask, nothing speculative.
export class DanvaModelProvider {
	// `models`: array of ModelDefinition (see contract above). Empty by
	// default - see createUnavailableDanvaModelProvider() below, which is
	// what this project actually uses today.
	constructor(models = []){
		this._models = models;
	}

	// True once at least one real model has been loaded - the one flag every
	// caller needs to decide "can I show real DANVA UI, or the honest
	// 'ingen DANVA-model indlæst' state" (task section 14/16/52).
	hasAnyModel(){
		return this._models.length > 0;
	}

	listModels(){
		return this._models.map((m) => ({model: m.model, version: m.version}));
	}

	getModelDefinition(model, version){
		return this._models.find((m) => m.model === model && m.version === version) || null;
	}

	getObjectTypes(model, version){
		const def = this.getModelDefinition(model, version);
		return def ? def.objectTypes : [];
	}

	getObjectDefinition(model, version, objectTypeId){
		const types = this.getObjectTypes(model, version);
		return types.find((t) => t.id === objectTypeId) || null;
	}

	getCodeList(model, version, codeListId){
		const def = this.getModelDefinition(model, version);
		return (def && def.codeLists && def.codeLists[codeListId]) || null;
	}
}

// The provider this project actually constructs today (see DanvaPanel.js) -
// zero models, every query resolves to "nothing" cleanly rather than
// throwing. Every DANVA UI path checks `hasAnyModel()` first and shows an
// honest "no model loaded, see [next step]" state instead of guessing at
// content (task section 53). Swapping this for a real provider, once
// licensed model files are available, is the ONE integration point - see
// createDanvaModelProviderFromModelData() below.
export function createUnavailableDanvaModelProvider(){
	return new DanvaModelProvider([]);
}

// Real, working constructor for when a licensed model package DOES become
// available - takes already-parsed ModelDefinition object(s) (however they
// were obtained: a bundled official JSON/JS extraction of the licensed
// model, a build step converting the official XSD/kodeliste files, etc. -
// deliberately out of this function's concern) and returns a provider
// exposing them through the exact same interface as the unavailable one, so
// nothing calling into DanvaModelProvider needs to change when real data
// arrives.
export function createDanvaModelProviderFromModelData(modelDefinitions){
	return new DanvaModelProvider(modelDefinitions);
}
